import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { Badge, Col, Row, Skeleton, Space, Typography } from 'antd';
import { ScopeTabs } from 'components/Custom/tabs';
import { ReactComponent as IconSupport } from 'assets/images/icons/iconSupport.svg';
import { ReactComponent as PadLockIcon } from 'assets/images/icons/padLockIcon.svg';
import { ReactComponent as RedWarningIcon } from 'assets/images/icons/redWarningIcon.svg';
import { ReactComponent as RightArrowGreenIcon } from 'assets/images/icons/rightArrowGreenIcon.svg';
import { RoundInput } from 'components/Custom/inputs';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import {
  CardTitleLargeSpan,
  ColoredTag,
  DashboardContainer,
  GrayItalicSpan,
  GraySpan,
} from 'components/Custom/spans';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { LocalizedNumber } from 'components/LocalizedNumber';
import { EmptyPage } from 'containers/EmptyPage';
import moment from 'moment';
import { ACTIVATED, QUALIFIED, VIES_TUTORIAL_HIDE } from 'pages/vies';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AdvancedUserRightsCodes } from 'store/app/enums';
import { User } from 'store/auth/models';
import { getBillingHasAdvancedRights } from 'store/billing/selectors';
import { Organisation } from 'store/organisation/models';
import { ViesInfo } from 'store/vat-checker/models';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { Preference, PreferenceActions } from 'utils/preferences';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { ViesGuide } from './guide';

interface QuilifiedEntitiesProps {
  viesInfos: ViesInfo[];
  loadCustomersAndSuppliers: (connectionId: string) => void;
  activateVatChecker: (connectionId: string, entityName: string) => void;
  deactivateVatChecker: (
    connectionId: string,
    entityName: string,
    entityId: string
  ) => void;
  loading: boolean;
  onEditEntity: (viesInfo: ViesInfo) => void;
  hideAll: boolean;
  currentUser?: User;
  activateTab: (key: string) => void;
  activeTabKey?: string;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
  noEntities: boolean;
  organisations?: Organisation[];
}

const { Text } = Typography;

export const QualifiedEntities: React.FC<QuilifiedEntitiesProps> = ({
  viesInfos,
  loadCustomersAndSuppliers,
  activateVatChecker,
  deactivateVatChecker,
  loading,
  onEditEntity,
  hideAll,
  currentUser,
  activateTab,
  activeTabKey,
  handlePreferences,
  noEntities,
  organisations,
}) => {
  const guidePanelHide = handlePreferences(PreferenceActions.GET, {
    name: VIES_TUTORIAL_HIDE,
  });
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const [source, setSource] = React.useState(viesInfos);
  const [showCard, setShowCard] = React.useState(!guidePanelHide && !pwaMobileNav);
  const hasAdvancedUserRights = useSelector(getBillingHasAdvancedRights);

  const onChange = (key: string) => {
    activateTab(key);
  };

  React.useEffect(() => {
    if (viesInfos && viesInfos !== source) {
      const filterd = viesInfos.filter((f) => f.providerName !== 'zoho'); // temporarily remove Zoho from vat checker
      setSource(filterd);
    }
  }, [viesInfos]);

  const search = (e) => {
    const value = e.target.value;
    if (value != '') {
      const filterTable = viesInfos?.filter((o) =>
        String(o['organisationName'])
          .toLowerCase()
          .includes(value.toLowerCase())
      );

      setSource(filterTable);
    } else {
      setSource(viesInfos);
    }
  };

  const sortQuilifiedEntities = (data) => {
    const sortedByName = data?.sort((a, b) =>
      a.organisationName.localeCompare(b.organisationName)
    );
    const sortedByStatus = sortedByName.sort((a, b) => {
      if (a.vatCheckerActive && !b.vatCheckerActive) {
        return -1;
      } else if (!a.vatCheckerActive && b.vatCheckerActive) {
        return 1;
      } else {
        return 0;
      }
    });
    return sortedByStatus;
  };

  const renderActivatedTab = () => {
    return (
      <>
        <Row
          style={{
            padding: '35px 0px 20px 0px',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '5px',
          }}
        >
          <Col style={{ display: 'flex', flexDirection: 'column' }}>
            <CardTitleLargeSpan style={{ marginBottom: 10 }}>
              {t('vat-checker.activated-entities')}
            </CardTitleLargeSpan>
          </Col>
          <Col
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <RoundInput
              width={'266px'}
              height={'40px'}
              prefix={<SearchOutlined />}
              size="large"
                placeholder={t('vat-checker.search-entities')}
              onChange={search}
            />
          </Col>
        </Row>
        {source.filter((entity) => entity.vatCheckerActive).length == 0 &&
        noEntities ? (
          <>
            {!hideAll ? (
              <EmptyPage
                titleText={t('vat-checker.no-active-entities')}
                actionText={t('organisation.empty-entities-action')}
                buttonText={t('vat-checker.qualified-entities')}
                actionCallback={() => activateTab(QUALIFIED)}
              />
            ) : (
              <div style={{ height: '100vh' }}></div>
            )}
          </>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                marginBottom: '25px',
                flexWrap: 'wrap',
                rowGap: '25px',
                columnGap: 20,
              }}
            >
              {sortQuilifiedEntities(
                source.filter((entity) => entity.vatCheckerActive)
              ).map((vi) => {
                const matchedOrg = organisations?.find(
                  (o) => o.connectionId === vi.connectionId
                );
                const lacksVatCheckerRight =
                  !!matchedOrg &&
                  hasAdvancedUserRights &&
                  !hasAdvancedUserRight(
                    currentUser,
                    matchedOrg as any,
                    AdvancedUserRightsCodes.VAT_NUMBER_CHECKER
                  );

                const container = (
                  <DashboardContainer
                    className={'active-container'}
                    onClick={
                      lacksVatCheckerRight
                        ? undefined
                        : () => loadCustomersAndSuppliers(vi.connectionId)
                    }
                    style={{
                      zIndex: 0,
                      ...(lacksVatCheckerRight
                        ? {
                            cursor: 'not-allowed',
                            backgroundColor: 'var(--color-surface, #f3f7f8)',
                          }
                        : {}),
                    }}
                  >
                    <Row
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '25px',
                      }}
                    >
                      <Col style={{ display: 'flex' }}>
                        <Space>
                          <ProviderLogoNav
                            providerName={vi.providerName}
                            shortCode={vi.shortCode}
                            organisationId={
                              organisations?.find(
                                (o) => o.connectionId === vi.connectionId
                              )?.uuid
                            }
                            style={{ height: '32px', display: 'block' }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <ProviderIcon
                              providerName={vi.providerName}
                              alt={vi.providerName || ''}
                              width={25}
                              style={{ paddingTop: '4px' }}
                            />
                          </ProviderLogoNav>
                          <div
                            style={{
                              width: '170%',
                            }}
                          >
                            <Text
                              className="container-title"
                              style={{ maxWidth: 150 }} //@ts-ignore
                              ellipsis={{ tooltip: vi.organisationName }}
                            >
                              {vi.organisationName}
                            </Text>
                          </div>
                          {/* Edit icon - commented out for now, may be reused later */}
                          {/* <div
                            className="edit-details-link"
                            style={{ paddingLeft: '10px', zIndex: 10 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditEntity(vi);
                            }}
                          >
                            {!lacksVatCheckerRight && <EditOutlined />}
                          </div> */}
                        </Space>
                      </Col>
                      <Col>
                        <StyledTooltip
                          title={t('vat-checker.click-to-view-customers-suppliers')}
                        >
                          <RightArrowGreenIcon
                            style={{ width: 22, height: 22, display: 'block' }}
                          />
                        </StyledTooltip>
                      </Col>
                    </Row>
                    <Row
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '25px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '38%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth:
                              vi.supplierCount < 100
                                ? 65
                                : vi.supplierCount < 1000
                                ? 75
                                : vi.supplierCount < 10000
                                ? 85
                                : 115,
                          }}
                        >
                          <Badge
                            count={
                              vi.hasInvalidSupplier ? (
                                <RedWarningIcon
                                  style={{
                                    width: 16,
                                    height: 16,
                                    display: 'block',
                                  }}
                                />
                              ) : null
                            }
                            offset={[2, 3]}
                          >
                            <LocalizedNumber
                              className="container-number-span"
                              value={vi.supplierCount}
                            />
                          </Badge>
                          <span className="container-number-title-span">
                            {t('vat-checker.suppliers')}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            minWidth:
                              vi.customerCount < 100
                                ? 65
                                : vi.customerCount < 1000
                                ? 75
                                : vi.customerCount < 10000
                                ? 85
                                : 115,
                          }}
                        >
                          <Badge
                            count={
                              vi.hasInvalidCustomer ? (
                                <RedWarningIcon
                                  style={{
                                    width: 16,
                                    height: 16,
                                    display: 'block',
                                  }}
                                />
                              ) : null
                            }
                            offset={[2, 3]}
                          >
                            <LocalizedNumber
                              className="container-number-span"
                              value={vi.customerCount}
                            />
                          </Badge>
                          <span className="container-number-title-span">
                            {t('vat-checker.customers')}
                          </span>
                        </div>
                      </div>
                      <Space
                        direction="vertical"
                        style={{
                          rowGap: 10,
                          marginTop: -5,
                          alignItems: 'flex-end',
                        }}
                      >
                        {vi.vatCheckerActive && (
                          <>
                            {vi.hasInvalidCustomer || vi.hasInvalidSupplier ? (
                              <>
                                <ColoredTag
                                  style={{ margin: 0 }}
                                  color="var(--color-status-danger, #D13131)" //@ts-ignore
                                  bgcolor="var(--color-status-danger-bg, #FCE7E7)"
                                  bordered={true}
                                  icon={
                                    <CloseOutlined
                                      style={{ color: '#D13131' }}
                                    />
                                  }
                                >
                                  {t('common.error')}
                                </ColoredTag>
                              </>
                            ) : (
                              <>
                                <ColoredTag
                                  style={{ margin: 0 }}
                                  color="var(--color-status-success, #058181)" //@ts-ignore
                                  bgcolor="var(--color-status-success-bg, #E9F6F7)"
                                  bordered={true}
                                  bordercolor="var(--color-border, #dbeae3)"
                                  icon={<CheckOutlined />}
                                >
                                  {t('main.valid')}
                                </ColoredTag>
                              </>
                            )}
                          </>
                        )}
                        <Space>
                          <GrayItalicSpan //@ts-ignore
                            fontSize="11px"
                            color="var(--color-text-base, #2e2e2e)"
                          >
                            {vi.lastViesCheckDate &&
                              moment(vi.lastViesCheckDate).fromNow()}
                          </GrayItalicSpan>
                          {vi.vatCheckerActive && currentUser && (
                            <>
                              {(() => {
                                const matchedOrgForDeactivate =
                                  organisations?.find(
                                    (o) => o.connectionId === vi.connectionId
                                  );
                                const lacksVatCheckerRightForDeactivate =
                                  !!matchedOrgForDeactivate &&
                                  hasAdvancedUserRights &&
                                  !hasAdvancedUserRight(
                                    currentUser,
                                    matchedOrgForDeactivate as any,
                                    AdvancedUserRightsCodes.VAT_NUMBER_CHECKER
                                  );

                                return (
                                  <div
                                    onClick={(e) => {
                                      // Stop click at this wrapper so the card
                                      // onClick doesn't fire, but Popconfirm still does.
                                      e.stopPropagation();
                                    }}
                                    style={{ display: 'inline-block' }}
                                  >
                                    <StyledPopconfirm //@ts-ignore
                                      title={t(
                                        'vat-checker.deactivating-entity-title'
                                      )}
                                      description={t(
                                        'vat-checker.deactivate-entity-description'
                                      )}
                                      onConfirm={() => {
                                        if (matchedOrgForDeactivate) {
                                          deactivateVatChecker(
                                            vi.connectionId,
                                            vi.organisationName,
                                            matchedOrgForDeactivate.uuid
                                          );
                                        }
                                      }}
                                      placement="top"
                                      okText={t('common.yes')}
                                      cancelText={t('common.cancel')}
                                    >
                                      <StyledTooltip
                                        placement="top"
                                        title={
                                          lacksVatCheckerRightForDeactivate
                                            ? t(
                                                'global.use-vat-checker-rights-warning'
                                              )
                                            : ''
                                        }
                                      >
                                        <span>
                                          <ScopeButton
                                            type="default" //@ts-ignore
                                            width="95px"
                                            height="25px"
                                            fontSize="12px"
                                            lineheight="12px"
                                            disabled={
                                              lacksVatCheckerRightForDeactivate
                                            }
                                          >
                                            {t('vat-checker.deactivate')}
                                          </ScopeButton>
                                        </span>
                                      </StyledTooltip>
                                    </StyledPopconfirm>
                                  </div>
                                );
                              })()}
                            </>
                          )}
                        </Space>
                      </Space>
                    </Row>
                  </DashboardContainer>
                );

                return lacksVatCheckerRight ? (
                  <StyledTooltip
                    key={vi.connectionId}
                    title={t('global.use-vat-checker-rights-warning')}
                    placement="top"
                  >
                    {container}
                  </StyledTooltip>
                ) : (
                  <React.Fragment key={vi.connectionId}>
                    {container}
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}
      </>
    );
  };
  const renderQualifiedTab = () => {
    return (
      <>
        {source.filter((entity) => !entity.vatCheckerActive).length > 0 && (
          <>
            <Row
              style={{
                padding: '35px 0px 20px 0px',
                borderTop: '1px solid var(--color-border, #dbeae3)',
                justifyContent: 'space-between',
              }}
            >
              <Col style={{ display: 'flex', flexDirection: 'column' }}>
                <CardTitleLargeSpan style={{ marginBottom: 10 }}>
                  {t('vat-checker.qualified-entities')}
                </CardTitleLargeSpan>
                <GraySpan>
                  {t('vat-checker.qualified-entities-subtitle')}
                </GraySpan>
              </Col>
              <Col
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <RoundInput
                  width={'266px'}
                  height={'40px'}
                  prefix={<SearchOutlined />}
                  size="large"
                  placeholder={t('vat-checker.search-entities')}
                  onChange={search}
                />
              </Col>
            </Row>
            <div
              style={{
                display: 'flex',
                marginBottom: '25px',
                flexWrap: 'wrap',
                rowGap: '25px',
                columnGap: 20,
              }}
            >
              {sortQuilifiedEntities(
                source.filter((entity) => !entity.vatCheckerActive)
              ).map((vi) => (
                <DashboardContainer
                  key={vi.connectionId}
                  className={'inactive-container'}
                >
                  <Row
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '25px',
                      cursor: 'default',
                    }}
                  >
                    <Col>
                      <Space>
                        <ProviderLogoNav
                          providerName={vi.providerName}
                          shortCode={vi.shortCode}
                          organisationId={
                            organisations?.find(
                              (o) => o.connectionId === vi.connectionId
                            )?.uuid
                          }
                          style={{ height: '32px', display: 'block' }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <ProviderIcon
                            providerName={vi.providerName}
                            alt={vi.providerName || ''}
                            width={25}
                            style={{ paddingTop: '4px' }}
                          />
                        </ProviderLogoNav>
                        <div
                          style={{
                            width: '170%',
                          }}
                        >
                          <Text
                            className="container-title"
                            style={{ maxWidth: 150 }} //@ts-ignore
                            ellipsis={{ tooltip: vi.organisationName }}
                          >
                            {vi.organisationName}
                          </Text>
                        </div>
                      </Space>
                    </Col>
                    <Col>
                      <PadLockIcon
                        style={{ width: 22, height: 22, display: 'block' }}
                      />
                    </Col>
                  </Row>
                  <Row
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '25px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '38%',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          minWidth:
                            vi.supplierCount < 100
                              ? 65
                              : vi.supplierCount < 1000
                              ? 75
                              : vi.supplierCount < 10000
                              ? 85
                              : 115,
                        }}
                      >
                        <Badge
                          count={
                            vi.hasInvalidSupplier ? (
                              <RedWarningIcon
                                style={{
                                  width: 16,
                                  height: 16,
                                  display: 'block',
                                }}
                              />
                            ) : null
                          }
                          offset={[2, 3]}
                        >
                          <LocalizedNumber
                            className="container-number-span"
                            value={vi.supplierCount}
                          />
                        </Badge>
                        <span className="container-number-title-span">
                          {t('vat-checker.suppliers')}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          minWidth:
                            vi.customerCount < 100
                              ? 65
                              : vi.customerCount < 1000
                              ? 75
                              : vi.customerCount < 10000
                              ? 85
                              : 115,
                        }}
                      >
                        <Badge
                          count={
                            vi.hasInvalidCustomer ? (
                              <RedWarningIcon
                                style={{
                                  width: 16,
                                  height: 16,
                                  display: 'block',
                                }}
                              />
                            ) : null
                          }
                          offset={[2, 3]}
                        >
                          <LocalizedNumber
                            className="container-number-span"
                            value={vi.customerCount}
                          />
                        </Badge>
                        <span className="container-number-title-span">
                          {t('vat-checker.customers')}
                        </span>
                      </div>
                    </div>
                    <Space direction="vertical" style={{ rowGap: 2 }}>
                      {!vi.vatCheckerActive && currentUser && (
                        <>
                          <br />
                          {(() => {
                            const matchedOrg = organisations?.find(
                              (o) => o.connectionId === vi.connectionId
                            );
                            const lacksVatCheckerRight =
                              !!matchedOrg &&
                              hasAdvancedUserRights &&
                              !hasAdvancedUserRight(
                                currentUser,
                                matchedOrg as any,
                                AdvancedUserRightsCodes.VAT_NUMBER_CHECKER
                              );

                            return (
                              <StyledTooltip
                                placement="top"
                                title={
                                  lacksVatCheckerRight
                                    ? t('global.use-vat-checker-rights-warning')
                                    : ''
                                }
                              >
                                <span>
                                  <ScopeButton
                                    type="dark-orange" //@ts-ignore
                                    width="78px"
                                    height="25px"
                                    fontSize="12px"
                                    lineheight="12px"
                                    onClick={() =>
                                      activateVatChecker(
                                        vi.connectionId,
                                        vi.organisationName
                                      )
                                    }
                                    disabled={lacksVatCheckerRight}
                                  >
                                    {t('billing.activate')}
                                  </ScopeButton>
                                </span>
                              </StyledTooltip>
                            );
                          })()}
                        </>
                      )}
                    </Space>
                  </Row>
                </DashboardContainer>
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  const TabItems = [
    {
      label: (
        <span>
          <CheckCircleOutlined style={{ marginRight: 8 }} />
          {t('vat-checker.activated')}
        </span>
      ),
      key: ACTIVATED,
      children: renderActivatedTab(),
    },
    {
      label: (
        <span>
          <FileTextOutlined style={{ marginRight: 8 }} />
          {t('vat-checker.qualified')}
        </span>
      ),
      key: QUALIFIED,
      children: renderQualifiedTab(),
    },
  ];

  return (
    <>
      {loading ? (
        <>
          {/* Guide button skeleton */}
          <Row
            justify={'end'}
            style={{
              marginTop: -5,
              marginBottom: 20,
            }}
          >
            <Skeleton.Button
              active
              size="default"
              style={{ width: '120px', height: '30px' }}
            />
          </Row>

          {/* Tabs skeleton */}
          <div style={{ marginBottom: '20px' }}>
            <Skeleton.Input
              active
              size="default"
              style={{ width: '150px', height: '40px' }}
            />
          </div>

          {/* Title and Search skeleton */}
          <Row
            style={{
              padding: '35px 0px 20px 0px',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '5px',
            }}
          >
            <Col style={{ display: 'flex', flexDirection: 'column' }}>
              <Skeleton.Input
                active
                size="default"
                style={{ width: '200px', height: '28px', marginBottom: 10 }}
              />
            </Col>
            <Col>
              <Skeleton.Input
                active
                size="default"
                style={{ width: '266px', height: '40px', borderRadius: '20px' }}
              />
            </Col>
          </Row>

          {/* Entity Cards Skeleton */}
          <div
            style={{
              display: 'flex',
              marginBottom: '25px',
              flexWrap: 'wrap',
              rowGap: '25px',
              columnGap: 20,
            }}
          >
            {[1, 2, 3].map((index) => (
              <DashboardContainer
                key={index}
                className={'active-container'}
                style={{
                  width: 'calc(33.333% - 14px)',
                  minWidth: '300px',
                }}
              >
                {/* Entity name row skeleton */}
                <Row
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '25px',
                  }}
                >
                  <Col
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    {/* Provider logo skeleton */}
                    <Skeleton.Avatar active size={30} shape="square" />
                    {/* Entity name skeleton */}
                    <Skeleton.Input
                      active
                      size="default"
                      style={{ width: '120px', height: '20px' }}
                    />
                    {/* Edit icon skeleton */}
                    <Skeleton.Button
                      active
                      size="small"
                      style={{
                        width: '16px',
                        height: '16px',
                        minWidth: '16px',
                      }}
                    />
                  </Col>
                  <Col>
                    <Skeleton.Button
                      active
                      size="small"
                      style={{
                        width: '24px',
                        height: '24px',
                        minWidth: '24px',
                      }}
                    />
                  </Col>
                </Row>

                {/* Suppliers and Customers row skeleton */}
                <Row
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '25px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '20px', width: '38%' }}>
                    {/* Suppliers skeleton */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Skeleton.Input
                        active
                        size="default"
                        style={{ width: '40px', height: '32px' }}
                      />
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: '65px', height: '16px' }}
                      />
                    </div>
                    {/* Customers skeleton */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Skeleton.Input
                        active
                        size="default"
                        style={{ width: '40px', height: '32px' }}
                      />
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: '70px', height: '16px' }}
                      />
                    </div>
                  </div>
                  {/* Error badge and timestamp skeleton */}
                  <Space direction="vertical" style={{ rowGap: 2 }}>
                    <Skeleton.Button
                      active
                      size="default"
                      style={{ width: '60px', height: '24px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '80px', height: '14px' }}
                    />
                  </Space>
                </Row>
              </DashboardContainer>
            ))}
          </div>
        </>
      ) : (
        <>
          {!pwaMobileNav &&
            (showCard ? (
              <ViesGuide
                setShowCard={setShowCard}
                handlePreferences={handlePreferences}
              />
            ) : (
              <Row
                justify={'end'}
                style={{
                  marginTop: -5,
                  marginBottom: 20,
                }}
              >
                <ExportButton
                  type="text"
                  onClick={() => {
                    setShowCard(true);
                    handlePreferences(PreferenceActions.SET, {
                      name: VIES_TUTORIAL_HIDE,
                      value: false,
                    });
                  }}
                  icon={<IconSupport style={{ width: 20, height: 20 }} />} //@ts-ignore
                  isGuide
                >
                  {t('organisation.open-guide')}
                </ExportButton>
              </Row>
            ))}
          <ScopeTabs items={TabItems} activeKey={activeTabKey} onChange={onChange} />
        </>
      )}
    </>
  );
};
